import { alpha, createTheme } from '@mui/material/styles';
import baseTheme from '../../theme';

const publicTheme = createTheme(baseTheme, {
  palette: {
    mode: 'light',
    primary: {
      main: '#059669',
      dark: '#047857',
      light: '#10B981',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#F59E0B',
      dark: '#D97706',
      light: '#FEF3C7',
      contrastText: '#111827',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#111827',
      secondary: '#4B5563',
    },
    divider: alpha('#111827', 0.12),
  },
  shape: {
    borderRadius: 0,
  },
  typography: {
    fontFamily: '"Montserrat", "Inter", sans-serif',
    h1: { fontFamily: '"Playfair Display", "Montserrat", serif', fontSize: '2.5rem', fontWeight: 700, lineHeight: 1.08, letterSpacing: '-0.02em' },
    h2: { fontFamily: '"Playfair Display", "Montserrat", serif', fontSize: '2.08rem', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.015em' },
    h3: { fontFamily: '"Playfair Display", "Montserrat", serif', fontSize: '1.7rem', fontWeight: 700, lineHeight: 1.18 },
    h4: { fontFamily: '"Playfair Display", "Montserrat", serif', fontSize: '1.32rem', fontWeight: 650, lineHeight: 1.26 },
    h5: { fontFamily: '"Montserrat", "Inter", sans-serif', fontSize: '1rem', fontWeight: 650, lineHeight: 1.32 },
    h6: { fontFamily: '"Montserrat", "Inter", sans-serif', fontSize: '0.92rem', fontWeight: 650, lineHeight: 1.34 },
    subtitle1: { fontSize: '0.92rem', fontWeight: 600, lineHeight: 1.45 },
    subtitle2: { fontSize: '0.84rem', fontWeight: 600, lineHeight: 1.4 },
    body1: { fontSize: '0.92rem', fontWeight: 500, lineHeight: 1.62 },
    body2: { fontSize: '0.84rem', fontWeight: 500, lineHeight: 1.58 },
    button: { fontFamily: '"Montserrat", "Inter", sans-serif', fontSize: '0.78rem', fontWeight: 700, textTransform: 'none', letterSpacing: 0.01 },
    caption: { fontSize: '0.7rem', fontWeight: 600, lineHeight: 1.34 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@import':
          "url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&family=Playfair+Display:wght@600;700&display=swap')",
        ':root': {
          '--public-accent': '#F59E0B',
          '--public-primary': '#059669',
          '--public-text': '#111827',
          '--public-border': 'rgba(17, 24, 39, 0.12)',
        },
        body: {
          backgroundColor: '#FFFFFF',
          backgroundImage: 'none',
          color: '#111827',
          textRendering: 'optimizeLegibility',
          '-webkit-font-smoothing': 'antialiased',
        },
        '::selection': {
          backgroundColor: alpha('#F59E0B', 0.22),
          color: '#111827',
        },
        '*::-webkit-scrollbar': {
          width: 8,
          height: 8,
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: alpha('#059669', 0.22),
          borderRadius: 0,
        },
        '*::-webkit-scrollbar-track': {
          backgroundColor: alpha('#059669', 0.06),
        },
        'img, picture img, canvas': {
          borderRadius: '0 !important',
        },
        '#root': {
          minHeight: '100vh',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#FFFFFF', 0.86),
          borderBottom: `1px solid ${alpha('#111827', 0.12)}`,
          backdropFilter: 'blur(16px)',
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          border: `1px solid ${alpha('#111827', 0.12)}`,
          borderRadius: 0,
          backgroundImage: 'none',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 6px 16px rgba(17, 24, 39, 0.06)',
        },
        outlined: {
          border: `1px solid ${alpha('#111827', 0.12)}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${alpha('#111827', 0.12)}`,
          borderRadius: 0,
          backgroundImage: 'none',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 6px 16px rgba(17, 24, 39, 0.06)',
        },
        outlined: {
          border: `1px solid ${alpha('#111827', 0.12)}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          minHeight: 40,
          paddingInline: 18,
          borderWidth: 1,
          transition: 'transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
          '&:active': {
            transform: 'translateY(1px)',
          },
        },
        contained: {
          boxShadow: `0 10px 20px ${alpha('#059669', 0.2)}`,
          '&:hover': {
            boxShadow: `0 12px 24px ${alpha('#059669', 0.24)}`,
          },
        },
        outlined: {
          borderColor: alpha('#059669', 0.24),
          '&:hover': {
            borderColor: alpha('#059669', 0.4),
            backgroundColor: alpha('#059669', 0.04),
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          border: `1px solid ${alpha('#111827', 0.12)}`,
          backgroundImage: 'none',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 24px 56px rgba(17, 24, 39, 0.18)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          fontWeight: 700,
          borderColor: alpha('#111827', 0.2),
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          transition: 'color 0.2s ease',
        },
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: 'xl',
      },
      styleOverrides: {
        root: {
          paddingLeft: 14,
          paddingRight: 14,
          '@media (min-width:600px)': {
            paddingLeft: 18,
            paddingRight: 18,
          },
          '@media (min-width:900px)': {
            paddingLeft: 24,
            paddingRight: 24,
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        regular: {
          minHeight: 58,
        },
      },
    },
  },
});

export default publicTheme;
